import React, { useState, useEffect, useCallback } from "react";
import styles from "./VsdcFailedRequestsScreen.module.scss";
import classNames from "classnames";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import AreYouSureModal from "../../../components/modals/areYouSureModal/AreYouSureModal";
import BaseModal from "../../../components/modals/baseModal/BaseModal";
import Button from "../../../components/button/Button";
import SelectItem from "../../../components/selectItem/SelectItem";
import moment from "moment";
import { isMobile } from "react-device-detect";
import {
  fetchVsdcFailedRequests,
  clearVsdcFailedRequests,
  clearVsdcFailedRequestsResetState,
  fetchTestDevices,
  submitTestInvoice,
  submitTestInvoiceResetState,
} from "../../../store/actions/admin/vsdcFailedRequests";

const LIMIT_OPTIONS = [
  { value: "10", label: "10 requests" },
  { value: "25", label: "25 requests" },
  { value: "50", label: "50 requests" },
  { value: "100", label: "100 requests" },
];

const VsdcFailedRequestsScreen = () => {
  const dispatch = useDispatch();
  const [clearModalOpen, setClearModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [limit, setLimit] = useState("100");
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [invoiceType, setInvoiceType] = useState("Normal");
  const [transactionType, setTransactionType] = useState("Sale");
  const [paymentType, setPaymentType] = useState("Cash");
  const [showTestSection, setShowTestSection] = useState(true);
  const [deviceSearchTerm, setDeviceSearchTerm] = useState("");
  const [manualMacAddress, setManualMacAddress] = useState("");
  const [manualIdentifier, setManualIdentifier] = useState("");
  const [useManualInput, setUseManualInput] = useState(false);

  const failedRequests = useSelector((state) => state.vsdcFailedRequests.failedRequests);
  const clearInfo = useSelector((state) => state.vsdcFailedRequests.clearRequests);
  const testDevices = useSelector((state) => state.vsdcFailedRequests.testDevices);
  const submitInvoiceInfo = useSelector((state) => state.vsdcFailedRequests.submitInvoice);

  const loadData = useCallback(() => {
    dispatch(fetchVsdcFailedRequests(parseInt(limit)));
  }, [dispatch, limit]);

  useEffect(() => {
    loadData();
    dispatch(fetchTestDevices());
  }, [loadData, dispatch]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadData();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, loadData]);

  useEffect(() => {
    if (clearInfo.success) {
      toast.success("Successfully cleared all failed requests");
      setClearModalOpen(false);
      dispatch(clearVsdcFailedRequestsResetState());
      loadData();
    }
  }, [clearInfo.success, dispatch, loadData]);

  useEffect(() => {
    if (clearInfo.error) {
      toast.error("Error clearing failed requests");
      dispatch(clearVsdcFailedRequestsResetState());
    }
  }, [clearInfo.error, dispatch]);

  useEffect(() => {
    if (submitInvoiceInfo.success) {
      toast.success("Test invoice submitted successfully!");
      dispatch(submitTestInvoiceResetState());
      loadData(); // Refresh failed requests list
    }
  }, [submitInvoiceInfo.success, dispatch, loadData]);

  useEffect(() => {
    if (submitInvoiceInfo.error) {
      toast.error("Error submitting test invoice");
      dispatch(submitTestInvoiceResetState());
    }
  }, [submitInvoiceInfo.error, dispatch]);

  useEffect(() => {
    if (testDevices.data?.devices?.length > 0 && !selectedDevice) {
      setSelectedDevice(testDevices.data.devices[0]);
      if (testDevices.data.devices[0].items?.length > 0) {
        setSelectedItem(testDevices.data.devices[0].items[0]);
      }
    }
  }, [testDevices.data, selectedDevice]);

  const handleClearConfirm = () => {
    dispatch(clearVsdcFailedRequests());
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setDetailsModalOpen(true);
  };

  const handleDeviceChange = (deviceId) => {
    const device = testDevices.data.devices.find(d => d.id === parseInt(deviceId));
    setSelectedDevice(device);
    if (device?.items?.length > 0) {
      setSelectedItem(device.items[0]);
    } else {
      setSelectedItem(null);
    }
  };

  const handleItemChange = (itemId) => {
    const item = selectedDevice?.items?.find(i => i.id === parseInt(itemId));
    setSelectedItem(item);
  };

  const handleSubmitTestInvoice = () => {
    let macAddress, identifier;

    if (useManualInput) {
      if (!manualMacAddress || !manualIdentifier) {
        toast.error("Please enter MAC address and identifier");
        return;
      }
      macAddress = manualMacAddress;
      identifier = manualIdentifier;
    } else {
      if (!selectedDevice || !selectedItem) {
        toast.error("Please select a device and item");
        return;
      }
      macAddress = selectedDevice.macAddress;
      identifier = selectedItem.identifier;
    }

    const invoiceData = {
      macAddress,
      identifier,
      invoiceType,
      transactionType,
      paymentType,
    };

    dispatch(submitTestInvoice(invoiceData));
  };

  const filteredDevices = testDevices.data?.devices?.filter(device => {
    const searchLower = deviceSearchTerm.toLowerCase();
    return (
      device.name.toLowerCase().includes(searchLower) ||
      device.macAddress.toLowerCase().includes(searchLower) ||
      device.fiscalDevice?.name.toLowerCase().includes(searchLower)
    );
  }) || [];

  const getStatusColor = (status) => {
    if (status >= 500) return "red";
    if (status >= 400) return "orange";
    return "gray";
  };

  const renderMetricCard = (title, value, subtitle) => {
    return (
      <div className={styles.metricCard}>
        <div className={styles.metricContent}>
          <p className={styles.metricValue}>{value ?? 0}</p>
          <p className={styles.metricTitle}>{title}</p>
          {subtitle && <p className={styles.metricSubtitle}>{subtitle}</p>}
        </div>
      </div>
    );
  };

  const renderRequestCard = (request, index) => {
    const timestamp = moment(request.timestamp).format("DD-MM-YYYY HH:mm:ss");
    const statusColor = getStatusColor(request.responseStatus);

    if (isMobile) {
      return (
        <div className={styles.requestCard} key={index}>
          <div className={styles.cardHeader}>
            <span className={classNames(styles.statusBadge, styles[statusColor])}>
              {request.responseStatus || "N/A"}
            </span>
            <span className={styles.timestamp}>{timestamp}</span>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.cardRow}>
              <span className={styles.label}>MAC Address:</span>
              <span className={styles.value}>{request.macAddress}</span>
            </div>
            <div className={styles.cardRow}>
              <span className={styles.label}>Invoice:</span>
              <span className={styles.value}>
                {request.requestData?.invoiceNumber || "N/A"}
              </span>
            </div>
            <div className={styles.cardRow}>
              <span className={styles.label}>Certificate:</span>
              <span className={styles.value}>{request.certificateId}</span>
            </div>
            {request.errorMessage && (
              <div className={styles.errorRow}>
                <span className={styles.errorLabel}>Error:</span>
                <span className={styles.errorValue}>{request.errorMessage}</span>
              </div>
            )}
          </div>
          <div className={styles.cardFooter}>
            <button
              className={styles.viewButton}
              onClick={() => handleViewDetails(request)}
            >
              View Details
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        className={classNames(
          styles.requestRow,
          index % 2 === 0 ? styles.even : styles.odd
        )}
        key={index}
      >
        <div className={styles.cell}>
          <p className={styles.timestamp}>{timestamp}</p>
        </div>
        <div className={styles.cell}>
          <span className={classNames(styles.statusBadge, styles[statusColor])}>
            {request.responseStatus || "N/A"}
          </span>
        </div>
        <div className={styles.cell}>
          <p className={styles.macAddress}>{request.macAddress}</p>
        </div>
        <div className={styles.cell}>
          <p>{request.requestData?.invoiceNumber || "N/A"}</p>
        </div>
        <div className={styles.cell}>
          <p className={styles.certificateId}>{request.certificateId}</p>
        </div>
        <div className={styles.cell}>
          <p className={styles.errorMessage}>
            {request.errorMessage || "No error message"}
          </p>
        </div>
        <div className={styles.cell}>
          <button
            className={styles.viewButton}
            onClick={() => handleViewDetails(request)}
          >
            View Details
          </button>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <AreYouSureModal
        modalOpen={clearModalOpen}
        closeModal={() => setClearModalOpen(false)}
        onConfirm={handleClearConfirm}
        title="Clear Failed Requests"
        message={
          <div>
            <p>Are you sure you want to clear all failed VSDC requests?</p>
            <p className={styles.warningText}>
              <strong>Warning:</strong> This action cannot be undone! All{" "}
              {failedRequests.data?.total || 0} failed requests will be removed.
            </p>
          </div>
        }
      />

      <BaseModal
        modalOpen={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedRequest(null);
        }}
        title="Request Details"
      >
        {selectedRequest && (
          <div className={styles.requestDetails}>
            <div className={styles.detailSection}>
              <h3 className={styles.sectionTitle}>General Information</h3>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Timestamp:</span>
                <span className={styles.detailValue}>
                  {moment(selectedRequest.timestamp).format("DD-MM-YYYY HH:mm:ss.SSS")}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>MAC Address:</span>
                <span className={styles.detailValue}>{selectedRequest.macAddress}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Identifier:</span>
                <span className={styles.detailValue}>{selectedRequest.identifier}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Certificate ID:</span>
                <span className={styles.detailValue}>{selectedRequest.certificateId}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>VSDC URL:</span>
                <span className={styles.detailValue}>{selectedRequest.vscdUrl}</span>
              </div>
            </div>

            <div className={styles.detailSection}>
              <h3 className={styles.sectionTitle}>Request</h3>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Method:</span>
                <span className={styles.detailValue}>{selectedRequest.method}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>URL:</span>
                <span className={styles.detailValue}>{selectedRequest.url}</span>
              </div>
              <div className={styles.jsonSection}>
                <span className={styles.detailLabel}>Request Data:</span>
                <pre className={styles.jsonData}>
                  {JSON.stringify(selectedRequest.requestData, null, 2)}
                </pre>
              </div>
            </div>

            <div className={styles.detailSection}>
              <h3 className={styles.sectionTitle}>Response</h3>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Status:</span>
                <span className={classNames(
                  styles.detailValue,
                  styles[getStatusColor(selectedRequest.responseStatus)]
                )}>
                  {selectedRequest.responseStatus || "N/A"}
                </span>
              </div>
              {selectedRequest.errorMessage && (
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Error Message:</span>
                  <span className={classNames(styles.detailValue, styles.error)}>
                    {selectedRequest.errorMessage}
                  </span>
                </div>
              )}
              <div className={styles.jsonSection}>
                <span className={styles.detailLabel}>Response Data:</span>
                <pre className={styles.jsonData}>
                  {JSON.stringify(selectedRequest.responseData, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        )}
      </BaseModal>

      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>VSDC Failed Requests</h1>
          <div className={styles.headerActions}>
            <label className={styles.autoRefreshLabel}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className={styles.checkbox}
              />
              Auto-refresh (30s)
            </label>
            <button className={styles.refreshButton} onClick={loadData}>
              ↻ Refresh
            </button>
          </div>
        </div>

        {showTestSection && (
          <div className={styles.testSection}>
            <div className={styles.testHeader}>
              <h2 className={styles.testTitle}>🧪 Test Invoice Submission</h2>
              <button
                className={styles.closeTestButton}
                onClick={() => setShowTestSection(false)}
              >
                ✕
              </button>
            </div>
            
            {testDevices.data?.devices?.length === 0 ? (
              <div className={styles.noDevices}>
                <p>No test devices available</p>
                <p className={styles.noDevicesSubtext}>
                  Devices need a fiscal device, certificate, and at least one item
                </p>
              </div>
            ) : (
              <div className={styles.testForm}>
                <div className={styles.inputModeSelector}>
                  <label className={styles.modeLabel}>
                    <input
                      type="radio"
                      name="inputMode"
                      checked={!useManualInput}
                      onChange={() => setUseManualInput(false)}
                      className={styles.radio}
                    />
                    Select from devices
                  </label>
                  <label className={styles.modeLabel}>
                    <input
                      type="radio"
                      name="inputMode"
                      checked={useManualInput}
                      onChange={() => setUseManualInput(true)}
                      className={styles.radio}
                    />
                    Manual input
                  </label>
                </div>

                {!useManualInput ? (
                  <>
                    <div className={styles.searchWrapper}>
                      <label className={styles.formLabel}>Search Devices:</label>
                      <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Search by name, MAC address, or fiscal device..."
                        value={deviceSearchTerm}
                        onChange={(e) => setDeviceSearchTerm(e.target.value)}
                      />
                    </div>

                    <div className={styles.formRow}>
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                          Device ({filteredDevices.length} results):
                        </label>
                        <select
                          className={styles.formSelect}
                          value={selectedDevice?.id || ""}
                          onChange={(e) => handleDeviceChange(e.target.value)}
                          size="5"
                        >
                          {filteredDevices.map((device) => (
                            <option key={device.id} value={device.id}>
                              {device.name} ({device.macAddress}) - {device.fiscalDevice?.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Item:</label>
                        <select
                          className={styles.formSelect}
                          value={selectedItem?.id || ""}
                          onChange={(e) => handleItemChange(e.target.value)}
                          disabled={!selectedDevice?.items?.length}
                        >
                          {selectedDevice?.items?.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.name} ({item.identifier}) - {item.unitPrice} RSD
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>MAC Address:</label>
                      <input
                        type="text"
                        className={styles.formInput}
                        placeholder="e.g., PROV_36EE18"
                        value={manualMacAddress}
                        onChange={(e) => setManualMacAddress(e.target.value)}
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>Identifier:</label>
                      <input
                        type="text"
                        className={styles.formInput}
                        placeholder="e.g., c002"
                        value={manualIdentifier}
                        onChange={(e) => setManualIdentifier(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Invoice Type:</label>
                    <select
                      className={styles.formSelect}
                      value={invoiceType}
                      onChange={(e) => setInvoiceType(e.target.value)}
                    >
                      <option value="Normal">Normal</option>
                      <option value="Training">Training</option>
                      <option value="Copy">Copy</option>
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Transaction Type:</label>
                    <select
                      className={styles.formSelect}
                      value={transactionType}
                      onChange={(e) => setTransactionType(e.target.value)}
                    >
                      <option value="Sale">Sale</option>
                      <option value="Refund">Refund</option>
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Payment Type:</label>
                    <select
                      className={styles.formSelect}
                      value={paymentType}
                      onChange={(e) => setPaymentType(e.target.value)}
                    >
                      <option value="Cash">Cash</option>
                      <option value="Card">Card</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                {((selectedDevice && selectedItem && !useManualInput) || 
                  (manualMacAddress && manualIdentifier && useManualInput)) && (
                  <div className={styles.testInfo}>
                    {!useManualInput ? (
                      <>
                        <div className={styles.infoRow}>
                          <span className={styles.infoLabel}>Device:</span>
                          <span className={styles.infoValue}>
                            {selectedDevice.name} ({selectedDevice.macAddress})
                          </span>
                        </div>
                        <div className={styles.infoRow}>
                          <span className={styles.infoLabel}>Fiscal Device:</span>
                          <span className={styles.infoValue}>
                            {selectedDevice.fiscalDevice?.name}
                          </span>
                        </div>
                        <div className={styles.infoRow}>
                          <span className={styles.infoLabel}>Certificate:</span>
                          <span className={styles.infoValue}>
                            {selectedDevice.certificate?.certificateId}
                          </span>
                        </div>
                        <div className={styles.infoRow}>
                          <span className={styles.infoLabel}>VSDC URL:</span>
                          <span className={styles.infoValue}>
                            {selectedDevice.certificate?.vscdUrl}
                          </span>
                        </div>
                        <div className={styles.infoRow}>
                          <span className={styles.infoLabel}>Item:</span>
                          <span className={styles.infoValue}>
                            {selectedItem.name} - {selectedItem.unitPrice} RSD
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={styles.infoRow}>
                          <span className={styles.infoLabel}>MAC Address:</span>
                          <span className={styles.infoValue}>
                            {manualMacAddress}
                          </span>
                        </div>
                        <div className={styles.infoRow}>
                          <span className={styles.infoLabel}>Identifier:</span>
                          <span className={styles.infoValue}>
                            {manualIdentifier}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                )}

                <div className={styles.testActions}>
                  <Button
                    name={submitInvoiceInfo.attempt ? "Submitting..." : "Submit Test Invoice"}
                    onClick={handleSubmitTestInvoice}
                    disabled={
                      submitInvoiceInfo.attempt ||
                      (!useManualInput && (!selectedDevice || !selectedItem)) ||
                      (useManualInput && (!manualMacAddress || !manualIdentifier))
                    }
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {!showTestSection && (
          <button
            className={styles.showTestButton}
            onClick={() => setShowTestSection(true)}
          >
            🧪 Show Test Section
          </button>
        )}

        <div className={styles.metricsGrid}>
          {renderMetricCard(
            "Total Failed Requests",
            failedRequests.data?.total,
            `Showing latest ${Math.min(parseInt(limit), failedRequests.data?.total || 0)}`
          )}
          {renderMetricCard(
            "Max Stored",
            "100",
            "Redis storage limit (FIFO)"
          )}
        </div>

        <div className={styles.controls}>
          <div className={styles.limitSelector}>
            <label className={styles.limitLabel}>Show:</label>
            <SelectItem
              options={LIMIT_OPTIONS}
              value={limit}
              onChange={(value) => {
                setLimit(value);
                dispatch(fetchVsdcFailedRequests(parseInt(value)));
              }}
            />
          </div>
          <Button
            name="Clear All Requests"
            onClick={() => setClearModalOpen(true)}
            disabled={failedRequests.data?.total === 0}
          />
        </div>

        <div className={styles.tableSection}>
          {!isMobile && (
            <div className={styles.tableHeader}>
              <div className={styles.headerCell}>Timestamp</div>
              <div className={styles.headerCell}>Status</div>
              <div className={styles.headerCell}>MAC Address</div>
              <div className={styles.headerCell}>Invoice</div>
              <div className={styles.headerCell}>Certificate</div>
              <div className={styles.headerCell}>Error</div>
              <div className={styles.headerCell}>Actions</div>
            </div>
          )}
          <div className={styles.tableBody}>
            {failedRequests.attempt && (
              <div className={styles.loading}>Loading...</div>
            )}
            {!failedRequests.attempt && failedRequests.data?.requests?.length === 0 && (
              <div className={styles.emptyState}>
                <p>No failed requests found</p>
                <p className={styles.emptySubtext}>
                  Failed VSDC requests will appear here automatically
                </p>
              </div>
            )}
            {!failedRequests.attempt &&
              failedRequests.data?.requests?.map((request, index) =>
                renderRequestCard(request, index)
              )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default VsdcFailedRequestsScreen;

